import { useEffect, useState, useRef } from 'react';

import delay from '@zenbusiness/application-commons-utils/delay';
import DialogContent from '@zenbusiness/zui/DialogContent';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import PropTypes from 'prop-types';
import { Link, useNavigate, generatePath } from 'react-router-dom';

import FormModal from '#client/components/FormModal';
import { ActionButtonsGrid } from '#client/components/Grid';
import { DB_APPLICATION_STATUS } from '#client/constants/application';
import { MODAL_NAMES } from '#client/constants/modals';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBankingPath from '#client/hooks/useBankingPath';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountId from '#client/hooks/useUnitAccountId';

import ReviewingApplicationLoader from './components/ReviewingApplicationLoader';
import StatusLoader from './components/StatusLoader';

import { APPLICATION_STATUS_ALLOW_LIST, UNDER_REVIEW_LIST } from './constants/status';
import {
  DEFAULT_TIMEOUT_OTHER_STATUS,
  INTERVAL_TIMEOUT_APPROVE_STATUS,
  INTERVAL_TIMEOUT_APPROVE_STATUS_MAX_COUNT
} from './constants/time';

export default function ApplicationSubmissionModal({ handleClose }) {
  const { appStatus, appStatusError, loading } = useApplicationStatusContext();
  const [showReviewingApplicationLoader, setShowReviewingApplicationLoader] = useState(true);
  const { refetch: refetchUnitAccountId, unitAccountId } = useUnitAccountId();
  const { refetch: refetchUnitAccount } = useUnitAccount();
  const [count, setCount] = useState(0);
  const id = useRef(null);
  const navigate = useNavigate();
  const bankingPath = useBankingPath();

  const handleApprove = () => {
    navigate(
      generatePath(`${bankingPath}/:unitAccountId`, {
        unitAccountId: unitAccountId ? 'dashboard-notfunded' : 'dashboard'
      })
    );
  };

  useEffect(() => {
    let interval;
    let timer;

    if (loading) {
      return () => {};
    }

    switch (appStatus) {
      /**
       * If application is approved, we need to wait for the unit account id
       */
      case DB_APPLICATION_STATUS.APPROVED:
        interval = setInterval(async () => {
          const response = await refetchUnitAccountId();
          if (response?.data?.partnerAccounts?.partnerAccountIds?.length > 0) {
            await refetchUnitAccount();
            setShowReviewingApplicationLoader(false);
            clearInterval(interval);
          }

          setCount((prevCount) => prevCount + 1);
        }, INTERVAL_TIMEOUT_APPROVE_STATUS);
        id.current = interval;
        break;
      default:
        /**
         * If application is not approved, we need to wait for a while
         */
        timer = delay(() => {
          setShowReviewingApplicationLoader(false);
        }, DEFAULT_TIMEOUT_OTHER_STATUS);
        break;
    }

    return () => {
      clearInterval(interval);
      if (typeof timer === 'function') {
        timer();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStatus]);

  useEffect(() => {
    if (count >= INTERVAL_TIMEOUT_APPROVE_STATUS_MAX_COUNT) {
      clearInterval(id.current);
      setShowReviewingApplicationLoader(false);
    }
  }, [count]);

  const { createCommonErrorToast } = useToastContext();

  const isApproved =
    !showReviewingApplicationLoader && appStatus === DB_APPLICATION_STATUS.APPROVED;
  const isUnderReview = !showReviewingApplicationLoader && UNDER_REVIEW_LIST.includes(appStatus);
  const isAwaitingDocument =
    !showReviewingApplicationLoader && appStatus === DB_APPLICATION_STATUS.AWAITING_DOCUMENTS;

  useEffect(() => {
    if (!loading && (appStatusError || !APPLICATION_STATUS_ALLOW_LIST.includes(appStatus))) {
      handleClose();
      createCommonErrorToast({ error: appStatusError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStatusError, appStatus, handleClose, loading]);

  return (
    <FormModal
      handleClose={handleClose}
      hasCloseIcon={false}
    >
      <DialogContent>
        {showReviewingApplicationLoader && <ReviewingApplicationLoader />}
        {isApproved && (
          <StatusLoader
            handleClose={handleClose}
            src="/animation/lottie/_application-approved.json"
            text="You're in! Now let's mail your card and fund your account."
            title="Approved!"
          />
        )}
        {isUnderReview && (
          <StatusLoader
            handleClose={handleClose}
            isStatic // use image (svg) instead lottie file
            src="/illustration/_application-reviewing.svg"
            text="We’ll email you in 1 to 3 business days. When approved, to start banking, log in to your dashboard with your ZenBusiness credentials."
            title="Your application is received and under review."
          />
        )}
        {isAwaitingDocument && (
          <StatusLoader
            handleClose={handleClose}
            isStatic
            src="/illustration/_application-reviewing.svg"
            text="Before we can finish reviewing your application, you need to provide more official documentation that can verify you or your business."
            title="It looks like we need more info for review."
          />
        )}
      </DialogContent>
      <ActionButtonsGrid>
        {isApproved && (
          <LoadingButton
            color="primary"
            fullWidth
            onClick={handleApprove}
            size="large"
            variant="contained"
          >
            Let&apos;s do it
          </LoadingButton>
        )}
        {isUnderReview && (
          <LoadingButton
            color="primary"
            fullWidth
            onClick={handleClose}
            size="large"
            variant="contained"
          >
            Go to dashboard
          </LoadingButton>
        )}
        {isAwaitingDocument && (
          <LoadingButton
            color="primary"
            component={Link}
            fullWidth
            size="large"
            to={`?modal=${MODAL_NAMES.AWAITING_DOCUMENTS}`}
            variant="contained"
          >
            Upload documents
          </LoadingButton>
        )}
      </ActionButtonsGrid>
    </FormModal>
  );
}

ApplicationSubmissionModal.propTypes = {
  handleClose: PropTypes.func.isRequired
};
